import React, { useState, type FormEvent, type ChangeEvent } from 'react';

import {
  type ContactFormData,
  MAX_NAME_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_SUBJECT_LENGTH,
  MAX_MESSAGE_LENGTH,
  MIN_MESSAGE_LENGTH,
} from '../../../../shared';
import { PageHead } from '../../components/PageHead/PageHead';
import { API_ENDPOINT } from '../../constants/api';

import * as styles from './Contact.css';

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

export function Contact() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user types
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > MAX_NAME_LENGTH) {
      newErrors.name = `Name must be less than ${MAX_NAME_LENGTH} characters`;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.length > MAX_EMAIL_LENGTH) {
      newErrors.email = `Email must be less than ${MAX_EMAIL_LENGTH} characters`;
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length > MAX_SUBJECT_LENGTH) {
      newErrors.subject = `Subject must be less than ${MAX_SUBJECT_LENGTH} characters`;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < MIN_MESSAGE_LENGTH) {
      newErrors.message = `Message must be at least ${MIN_MESSAGE_LENGTH} characters`;
    } else if (formData.message.length > MAX_MESSAGE_LENGTH) {
      newErrors.message = `Message must be less than ${MAX_MESSAGE_LENGTH} characters`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus('success');
        setSubmitMessage(
          data.message || 'Thank you! We will get back to you soon.',
        );
        // Reset form
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
        setSubmitMessage(
          data.error ||
          'Something went wrong. Please try again or email us directly.',
        );
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setSubmitMessage(
        'Unable to send message. Please check your internet connection or email us directly at info@faeforest.com.au',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <PageHead
        title="Contact Us"
        description="Get in touch with Fae Forest about products, custom orders, and commissions."
        path="/contact"
      />
      <h1 className={styles.heading}>Contact Us</h1>
      <p className={styles.description}>
        Have a question about our products? Want to commission a custom piece?
        We'd love to hear from you! Send us a message and we'll get back to you
        as soon as possible.
      </p>

      {submitStatus === 'success' && (
        <div className={styles.successMessage}>
          <strong>Success!</strong> {submitMessage}
        </div>
      )}

      {submitStatus === 'error' && (
        <div className={styles.errorMessage}>
          <strong>Error:</strong> {submitMessage}
        </div>
      )}

      {submitStatus !== 'success' && (
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="name" className={styles.label}>
              Name <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={styles.input}
              maxLength={MAX_NAME_LENGTH}
              disabled={isSubmitting}
            />
            {errors.name && <div className={styles.error}>{errors.name}</div>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              Email <span className={styles.required}>*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={styles.input}
              maxLength={MAX_EMAIL_LENGTH}
              disabled={isSubmitting}
            />
            {errors.email && <div className={styles.error}>{errors.email}</div>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="subject" className={styles.label}>
              Subject <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={styles.input}
              maxLength={MAX_SUBJECT_LENGTH}
              disabled={isSubmitting}
              placeholder="e.g., Product inquiry, Custom order, etc."
            />
            {errors.subject && (
              <div className={styles.error}>{errors.subject}</div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="message" className={styles.label}>
              Message <span className={styles.required}>*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className={styles.textarea}
              maxLength={MAX_MESSAGE_LENGTH}
              disabled={isSubmitting}
              placeholder="Tell us about your inquiry..."
            />
            <div className={styles.charCount}>
              {formData.message.length} / {MAX_MESSAGE_LENGTH}
            </div>
            {errors.message && (
              <div className={styles.error}>{errors.message}</div>
            )}
          </div>

          <button
            type="submit"
            className={styles.button}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      )}
      <p className={styles.description} style={{ marginTop: '2rem' }}>
        <strong>Direct Email:</strong> info@faeforest.com.au
        <br />
        <strong>Response Time:</strong> We typically respond within 2 business
        days.
      </p>
    </div>
  );
}
